import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { formatError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import TimezoneSelect from "../components/TimezoneSelect";
import { detectBrowserTz, tzDisplay } from "../lib/timezone";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../components/ui/alert-dialog";

const BG =
  "https://images.unsplash.com/photo-1782945217386-300f33f22069?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzd8MHwxfHNlYXJjaHwxfHxlbWVyZ2VuY3klMjBkaXNwYXRjaCUyMGNvbW1hbmQlMjBjZW50ZXIlMjBuaWdodHxlbnwwfHx8fDE3ODU4NTI3Mjd8MA&ixlib=rb-4.1.0&q=85";

export default function Login() {
  const { login, register } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ name: "", email: "", password: "", org_name: "", timezone: detectBrowserTz() });
  const [tzConfirmed, setTzConfirmed] = useState(false);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [leaveOpen, setLeaveOpen] = useState(false);

  const toLogin = () => {
    setMode("login");
    setError("");
    setTzConfirmed(false);
    setForm({ name: "", email: "", password: "", org_name: "", timezone: detectBrowserTz() });
  };

  const signupDirty = () =>
    !!(form.name.trim() || form.email.trim() || form.password.trim() || form.org_name.trim());

  const handleBackToSignIn = () => {
    if (signupDirty()) setLeaveOpen(true);
    else toLogin();
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (mode === "signup") {
      if (!form.timezone) { setError("Please select your organization's timezone."); return; }
      if (!tzConfirmed) { setError("Please confirm your organization's timezone before continuing."); return; }
    }
    setBusy(true);
    try {
      if (mode === "login") {
        await login(form.email, form.password);
      } else {
        await register(form);
      }
      toast.success("Welcome to WatchRelay");
      navigate("/dashboard");
    } catch (err) {
      setError(formatError(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="grid min-h-screen lg:grid-cols-2">
      {/* Left visual */}
      <div className="relative hidden lg:block">
        <img src={BG} alt="Facility at night" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-slate-950/80" />
        <div className="relative flex h-full flex-col justify-between p-12 text-white">
          <div className="flex items-center gap-3">
            <img src="/watchrelay-shield.png" alt="WatchRelay" className="h-12 w-12 object-contain" />
            <span className="font-display text-2xl font-black">Watch<span className="text-blue-400">Relay</span></span>
          </div>
          <div className="max-w-md">
            <h1 className="font-display text-4xl font-black leading-tight tracking-tight">
              Never lose a critical detail between shifts.
            </h1>
            <p className="mt-4 text-slate-300 leading-relaxed">
              Purpose-built shift handoffs for public-safety operations. Pass operational updates,
              equipment issues, and unfinished tasks securely from one shift to the next.
            </p>
          </div>
          <p className="text-xs uppercase tracking-[0.3em] text-slate-500">Fictional Public-Safety Demo Environment</p>
        </div>
      </div>

      {/* Right form */}
      <div className="flex items-center justify-center bg-white p-6 sm:p-12">
        <div className="w-full max-w-sm animate-fade-up">
          <div className="lg:hidden mb-8 flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-md bg-slate-900">
              <img src="/watchrelay-shield.png" alt="WatchRelay" className="h-7 w-7 object-contain" />
            </div>
            <span className="font-display text-xl font-black text-slate-900">Watch<span className="text-blue-600">Relay</span></span>
          </div>

          {mode === "signup" && (
            <button
              type="button"
              data-testid="back-to-signin"
              onClick={handleBackToSignIn}
              className="mb-4 inline-flex items-center gap-1.5 rounded text-sm font-medium text-slate-500 transition-colors hover:text-slate-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2"
            >
              <ArrowLeft className="h-4 w-4" />
              <span className="hidden sm:inline">Back to Sign In</span>
              <span className="sm:hidden">Sign In</span>
            </button>
          )}

          <h2 className="font-display text-3xl font-black tracking-tight text-slate-900">
            {mode === "login" ? "Sign in" : "Create organization"}
          </h2>
          <p className="mt-2 text-sm text-slate-500">
            {mode === "login"
              ? "Access your shift handoff dashboard."
              : "Start a new team workspace as its admin."}
          </p>

          <form onSubmit={submit} className="mt-8 space-y-4">
            {mode === "signup" && (
              <>
                <div>
                  <Label htmlFor="name" className="text-xs font-bold uppercase tracking-wider text-slate-500">
                    Full name
                  </Label>
                  <Input id="name" data-testid="signup-name-input" value={form.name} onChange={set("name")} required className="mt-1.5" />
                </div>
                <div>
                  <Label htmlFor="org" className="text-xs font-bold uppercase tracking-wider text-slate-500">
                    Organization name
                  </Label>
                  <Input id="org" data-testid="signup-org-input" value={form.org_name} onChange={set("org_name")} required className="mt-1.5" />
                </div>
                <div>
                  <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">
                    Organization timezone
                  </Label>
                  <div className="mt-1.5">
                    <TimezoneSelect value={form.timezone} onChange={(tz) => { setForm({ ...form, timezone: tz }); setTzConfirmed(false); }} testid="signup-timezone-select" />
                  </div>
                  <p className="mt-1.5 text-xs text-slate-400">Suggested from your browser. All handoff and schedule times will display in this timezone.</p>
                  <label className="mt-2 flex items-start gap-2 text-xs text-slate-600">
                    <input
                      type="checkbox"
                      data-testid="signup-timezone-confirm"
                      checked={tzConfirmed}
                      onChange={(e) => setTzConfirmed(e.target.checked)}
                      className="mt-0.5 h-4 w-4 rounded border-slate-300"
                    />
                    <span>I confirm <span className="font-semibold text-slate-800">{tzDisplay(form.timezone)}</span> is my organization's timezone.</span>
                  </label>
                </div>
              </>
            )}
            <div>
              <Label htmlFor="email" className="text-xs font-bold uppercase tracking-wider text-slate-500">
                Email
              </Label>
              <Input id="email" type="email" data-testid="login-email-input" value={form.email} onChange={set("email")} required className="mt-1.5" />
            </div>
            <div>
              <Label htmlFor="password" className="text-xs font-bold uppercase tracking-wider text-slate-500">
                Password
              </Label>
              <Input id="password" type="password" data-testid="login-password-input" value={form.password} onChange={set("password")} required className="mt-1.5" />
              {mode === "login" && (
                <div className="mt-1.5 text-right">
                  <Link to="/forgot-password" data-testid="forgot-password-link" className="text-xs font-medium text-blue-600 hover:text-blue-700 transition-colors">
                    Forgot password?
                  </Link>
                </div>
              )}
            </div>

            {error && (
              <p data-testid="login-error" className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-600 border border-red-200">
                {error}
              </p>
            )}

            <Button
              type="submit"
              data-testid="login-submit-button"
              disabled={busy || (mode === "signup" && !tzConfirmed)}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold"
            >
              {busy ? "Please wait…" : mode === "login" ? "Sign in" : "Create workspace"}
            </Button>
          </form>
        </div>
      </div>

      <AlertDialog open={leaveOpen} onOpenChange={setLeaveOpen}>
        <AlertDialogContent data-testid="leave-setup-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Leave organization setup?</AlertDialogTitle>
            <AlertDialogDescription>Your entered information will not be saved.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="stay-here-button">Stay Here</AlertDialogCancel>
            <AlertDialogAction data-testid="leave-setup-button" onClick={toLogin} className="bg-blue-600 hover:bg-blue-700 text-white">Leave Setup</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
